import { createAction, createAsyncThunk, Dispatch } from "@reduxjs/toolkit";
import {
  CancelImportApi,
  DeleteImportApi,
  DownloadSheetApi,
  EstimatePushApi,
  GetBusinessContextApi,
  GetCrmsApi,
  GetImportApi,
  GetUserWorkspacesApi,
  GetWorkspaceBusinessesApi,
  JobberConnectApi,
  ListImportsApi,
  PreviewPushApi,
  RetryImportApi,
  ReviewPushApi,
  SearchUsersApi,
  StartPullApi,
  StartPushApi,
  TestCrmConnectionApi,
} from "../../apis/AdminApis/AdminCrmImportApi";
import { showSnackbar } from "../../slices/SnackbarSlice";
import {
  Credentials,
  CrmRejection,
  DownloadSheetPayload,
  ImportListFilters,
  ImportListResult,
  ImportRow,
  LookupBusiness,
  LookupWorkspace,
  PullStartPayload,
  PushStartPayload,
  TestConnectionPayload,
  TestConnectionResult,
  UserSearchParams,
  UserSearchResult,
} from "../../../components/Admin/AdminCrmImport/crmImport.types";

const SILENT_ERROR_CODES = ["REVIEW_REQUIRED"];
const SHEET_EXPIRED_MESSAGE = "Sheet expired, run the pull again";

const toRejection = (error: any, fallback: string): CrmRejection => {
  const body = error?.response?.data;
  return {
    message: body?.message || fallback,
    code: body?.error?.code ?? null,
    field: body?.error?.field ?? null,
  };
};

const notifyError = (dispatch: Dispatch, rejection: CrmRejection) => {
  if (rejection.code && SILENT_ERROR_CODES.includes(rejection.code)) return;
  dispatch(showSnackbar({ message: rejection.message, type: "error" }));
};

interface CrmThunkOptions {
  silent?: boolean;
  successMessage?: string;
}

const createCrmThunk = <Returned, Arg = void>(
  type: string,
  fallbackMessage: string,
  request: (arg: Arg) => Promise<Returned>,
  { silent = false, successMessage }: CrmThunkOptions = {}
) =>
  createAsyncThunk<Returned, Arg, { rejectValue: CrmRejection }>(type, async (arg, { rejectWithValue, dispatch }) => {
    try {
      const result = await request(arg);
      if (successMessage) dispatch(showSnackbar({ message: successMessage, type: "success" }));
      return result;
    } catch (error: any) {
      const rejection = toRejection(error, fallbackMessage);
      if (!silent) notifyError(dispatch, rejection);
      return rejectWithValue(rejection);
    }
  });

const saveBlob = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

const trimCredentials = (credentials: Credentials): Credentials =>
  Object.fromEntries(Object.entries(credentials).map(([key, value]) => [key, value.trim()]));

export const crmImportRowUpserted = createAction<ImportRow>("admin/crm-import/row-upserted");

export const fetchCrmsAction = createCrmThunk("admin/crm-import/crms", "Could not load CRMs", async () => {
  const response = await GetCrmsApi();
  return response.data.data.crms;
});

export const searchCrmUsersAction = createCrmThunk<UserSearchResult, UserSearchParams>(
  "admin/crm-import/lookup-users",
  "Could not search users",
  async (params) => {
    const response = await SearchUsersApi(params);
    return { users: response.data.data.users, pagination: response.data.pagination ?? null };
  },
  { silent: true }
);

export const fetchCrmWorkspacesAction = createCrmThunk<LookupWorkspace[], string>(
  "admin/crm-import/lookup-workspaces",
  "Could not load workspaces",
  async (userId) => (await GetUserWorkspacesApi(userId)).data.data.workspaces
);

export const fetchCrmBusinessesAction = createCrmThunk<LookupBusiness[], string>(
  "admin/crm-import/lookup-businesses",
  "Could not load businesses",
  async (workspaceId) => (await GetWorkspaceBusinessesApi(workspaceId)).data.data.businesses
);

export const fetchCrmBusinessContextAction = createCrmThunk(
  "admin/crm-import/lookup-context",
  "Could not load business details",
  async (businessId: string) => (await GetBusinessContextApi(businessId)).data.data
);

export const listCrmImportsAction = createCrmThunk<ImportListResult, ImportListFilters>(
  "admin/crm-import/list",
  "Could not load import history",
  async (filters) => {
    const response = await ListImportsApi(filters);
    return { ...response.data.data, pagination: response.data.pagination ?? null, fetchedAt: Date.now() };
  },
  { silent: true }
);

export const getCrmImportAction = createCrmThunk(
  "admin/crm-import/detail",
  "Could not load import",
  async (id: string) => (await GetImportApi(id)).data.data.import,
  { silent: true }
);

export const cancelCrmImportAction = createCrmThunk(
  "admin/crm-import/cancel",
  "Could not cancel import",
  async (id: string) => (await CancelImportApi(id)).data.data.import,
  { successMessage: "Import cancelled" }
);

export const deleteCrmImportAction = createCrmThunk(
  "admin/crm-import/delete",
  "Could not delete import",
  async (id: string) => (await DeleteImportApi(id)).data.data.id,
  { successMessage: "Import deleted" }
);

export const downloadCrmSheetAction = createCrmThunk(
  "admin/crm-import/download-sheet",
  "Could not download the sheet",
  async ({ id, seq }: DownloadSheetPayload) => {
    try {
      const response = await DownloadSheetApi(id);
      saveBlob(response.data, `import-${seq}.xlsx`);
    } catch (error: any) {
      if (error?.response?.status === 404 || error?.response?.data?.error?.code === "SHEET_EXPIRED") {
        throw { response: { data: { message: SHEET_EXPIRED_MESSAGE, error: { code: "SHEET_EXPIRED" } } } };
      }
      throw error;
    }
  }
);

export const previewCrmPushAction = createCrmThunk(
  "admin/crm-import/push-preview",
  "Could not read the sheet",
  async (file: File) => (await PreviewPushApi(file)).data.data
);

export const estimateCrmPushAction = createCrmThunk(
  "admin/crm-import/push-estimate",
  "Could not estimate the schedule",
  async (payload: Parameters<typeof EstimatePushApi>[0]) => (await EstimatePushApi(payload)).data.data.estimate,
  { silent: true }
);

export const connectJobberAction = createCrmThunk(
  "admin/crm-import/jobber-connect",
  "Could not start the Jobber connection",
  async (businessId: string) => (await JobberConnectApi(businessId)).data.data
);

export const reviewCrmPushAction = createCrmThunk(
  "admin/crm-import/push-review",
  "Could not prepare the review",
  async (payload: Parameters<typeof ReviewPushApi>[0]) => (await ReviewPushApi(payload)).data.data
);

export const startCrmPushAction = createCrmThunk(
  "admin/crm-import/push-start",
  "Could not start the push",
  async (payload: PushStartPayload) => (await StartPushApi(payload)).data.data.import
);

// Credential-bearing calls are plain thunks on purpose: createAsyncThunk would copy the
// arguments into meta.arg of every dispatched action, exposing secrets to Redux tooling.
type CredentialThunk<T> = (dispatch: Dispatch) => Promise<T>;

const runCredentialRequest = async <T>(dispatch: Dispatch, fallback: string, request: () => Promise<T>): Promise<T> => {
  try {
    return await request();
  } catch (error: any) {
    const rejection = toRejection(error, fallback);
    notifyError(dispatch, rejection);
    throw rejection;
  }
};

export const testCrmConnectionAction =
  (payload: TestConnectionPayload): CredentialThunk<TestConnectionResult> =>
  (dispatch) =>
    runCredentialRequest(dispatch, "Could not test the connection", async () => {
      const response = await TestCrmConnectionApi({ ...payload, credentials: trimCredentials(payload.credentials) });
      return response.data.data;
    });

export const startCrmPullAction =
  (payload: PullStartPayload): CredentialThunk<ImportRow> =>
  (dispatch) =>
    runCredentialRequest(dispatch, "Could not start the fetch", async () => {
      const response = await StartPullApi({ ...payload, credentials: trimCredentials(payload.credentials) });
      const row = response.data.data.import;
      dispatch(crmImportRowUpserted(row));
      return row;
    });

export const retryCrmImportAction =
  (id: string, credentials?: Credentials): CredentialThunk<ImportRow> =>
  (dispatch) =>
    runCredentialRequest(dispatch, "Could not retry the import", async () => {
      const response = await RetryImportApi(id, credentials && trimCredentials(credentials));
      const row = response.data.data.import;
      dispatch(crmImportRowUpserted(row));
      return row;
    });
